import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { TrendingUp, Activity, RefreshCw, BarChart3 } from 'lucide-react';

interface EnhancedPerformanceAnalysisProps {
  timeRange: string;
}

export const EnhancedPerformanceAnalysis: React.FC<EnhancedPerformanceAnalysisProps> = ({ timeRange }) => {
  const { user } = useAuth();

  const { data: performanceData, isLoading, refetch } = useQuery({
    queryKey: ['enhanced-performance-analysis', user?.id, timeRange],
    queryFn: async () => {
      if (!user) return null;

      const { data: recentRuns } = await supabase
        .from('running_performance')
        .select('*')
        .eq('user_id', user.id)
        .order('recorded_date', { ascending: false })
        .limit(20);

      if (!recentRuns || recentRuns.length === 0) {
        return null;
      }

      // Calculate performance metrics
      const totalDistance = recentRuns.reduce((sum, run) => sum + (run.distance_meters || 0), 0) / 1000;
      const avgPace = recentRuns.length > 0 ? 
        recentRuns.reduce((sum, run) => sum + (run.pace_per_km_seconds || 0), 0) / recentRuns.length : 0;
      const avgHeartRate = recentRuns.filter(r => r.heart_rate_avg).length > 0 ?
        recentRuns.filter(r => r.heart_rate_avg).reduce((sum, run) => sum + (run.heart_rate_avg || 0), 0) / recentRuns.filter(r => r.heart_rate_avg).length : 0;

      // Performance trends
      const recentPaces = recentRuns.slice(0, 5).map(r => r.pace_per_km_seconds).filter(Boolean);
      const olderPaces = recentRuns.slice(5, 10).map(r => r.pace_per_km_seconds).filter(Boolean);
      
      const recentAvgPace = recentPaces.length > 0 ? recentPaces.reduce((a, b) => a + b, 0) / recentPaces.length : 0;
      const olderAvgPace = olderPaces.length > 0 ? olderPaces.reduce((a, b) => a + b, 0) / olderPaces.length : 0;
      
      const paceImprovement = olderAvgPace > 0 && recentAvgPace > 0 ? ((olderAvgPace - recentAvgPace) / olderAvgPace) * 100 : 0;

      return {
        totalDistance,
        avgPace,
        avgHeartRate,
        paceImprovement,
        runCount: recentRuns.length,
        recentRuns: recentRuns.slice(0, 5)
      };
    },
    enabled: !!user,
    staleTime: 10 * 60 * 1000,
  });

  const formatPace = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            Performance Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-20 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!performanceData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            Performance Analysis
          </CardTitle>
          <CardDescription>Advanced performance metrics and trends</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Activity className="h-12 w-12 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-500 mb-4">Need more running data for performance analysis</p>
            <Button onClick={() => refetch()} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-l-4 border-l-green-500">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            <CardTitle>Performance Analysis</CardTitle>
            <Badge variant="outline" className="bg-green-50 text-green-700 text-xs">
              Live Data
            </Badge>
          </div>
          <Button onClick={() => refetch()} variant="ghost" size="sm">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <CardDescription>Advanced performance metrics and trends</CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Performance Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">Total Distance</span>
            </div>
            <div className="text-2xl font-bold text-blue-700">
              {performanceData.totalDistance.toFixed(1)} km
            </div>
            <p className="text-xs text-blue-600">Recent {performanceData.runCount} runs</p>
          </div>

          <div className="bg-orange-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-4 w-4 text-orange-600" />
              <span className="text-sm font-medium">Average Pace</span>
            </div>
            <div className="text-2xl font-bold text-orange-700">
              {performanceData.avgPace > 0 ? formatPace(performanceData.avgPace) : 'N/A'}
            </div>
            <p className="text-xs text-orange-600">per kilometer</p>
          </div>

          <div className="bg-red-50 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium">Pace Trend</span>
            </div>
            <div className="text-2xl font-bold text-red-700">
              {performanceData.paceImprovement !== 0 ? (
                <>
                  {performanceData.paceImprovement > 0 ? '+' : ''}{performanceData.paceImprovement.toFixed(1)}%
                </>
              ) : 'N/A'}
            </div>
            <p className="text-xs text-red-600">
              {performanceData.paceImprovement > 0 ? 'Improving' : performanceData.paceImprovement < 0 ? 'Declining' : 'Stable'}
            </p>
          </div>
        </div>

        {/* Heart Rate Analysis */}
        {performanceData.avgHeartRate > 0 && (
          <div className="bg-purple-50 p-4 rounded-lg">
            <h4 className="font-medium text-sm mb-3 text-purple-900">Cardiovascular Performance</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-purple-600">Average Heart Rate</span>
                <div className="text-xl font-bold text-purple-700">
                  {Math.round(performanceData.avgHeartRate)} BPM
                </div>
              </div>
              <div>
                <span className="text-sm text-purple-600">Intensity Zone</span>
                <div className="text-xl font-bold text-purple-700">
                  {performanceData.avgHeartRate < 140 ? 'Low' : 
                   performanceData.avgHeartRate < 160 ? 'Moderate' : 'High'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Recent Performance Summary */}
        <div>
          <h4 className="font-medium text-sm mb-3">Recent Performance Summary</h4>
          <div className="space-y-2">
            {performanceData.recentRuns.slice(0, 3).map((run, index) => (
              <div key={run.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className="text-xs">
                    {new Date(run.recorded_date).toLocaleDateString()}
                  </Badge>
                  <span className="text-sm font-medium">
                    {(run.distance_meters / 1000).toFixed(1)}km
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  {run.pace_per_km_seconds ? formatPace(run.pace_per_km_seconds) + '/km' : 'No pace data'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};